<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import type { NuxtError } from '#app';
import { DEFAULT_LOCALE } from '~/constants/i18n';
import { getLocaleMeta, normalizeLocale } from '~/utils/i18n/locale';

const props = defineProps<{
  error: NuxtError;
}>();

const { t, locale } = useI18n();
const authStore = useAuthStore();
const isAuthed = computed(() => !!authStore.user);

// error.vue is Nuxt's separate root for the error page — app.vue (and its
// htmlAttrs lang/dir head) never renders here, so without this the page (and
// the rtl:-scale-x-100 "Go back" arrow below) never gets a dir="rtl" ancestor
// in RTL locales.
const localeMeta = computed(() => getLocaleMeta(normalizeLocale(locale.value) ?? DEFAULT_LOCALE));
useHead({
  htmlAttrs: {
    lang: computed(() => localeMeta.value.language),
    dir: computed(() => localeMeta.value.dir),
  },
});

type ErrorCopyKeys = {
  eyebrowKey: string;
  titleKey: string;
  descriptionKey: string;
  showCode?: boolean;
};

type ErrorCopy = {
  eyebrow: string;
  title: string;
  description: string;
  showCode?: boolean;
};

const COPY: Record<number, ErrorCopyKeys> = {
  400: {
    eyebrowKey: 'shell.error.badRequest.eyebrow',
    titleKey: 'shell.error.badRequest.title',
    descriptionKey: 'shell.error.badRequest.description',
    showCode: true,
  },
  403: {
    eyebrowKey: 'shell.error.forbidden.eyebrow',
    titleKey: 'shell.error.forbidden.title',
    descriptionKey: 'shell.error.forbidden.description',
    showCode: true,
  },
  404: {
    eyebrowKey: 'shell.error.notFound.eyebrow',
    titleKey: 'shell.error.notFound.title',
    descriptionKey: 'shell.error.notFound.description',
    showCode: true,
  },
  500: {
    eyebrowKey: 'shell.error.serverError.eyebrow',
    titleKey: 'shell.error.serverError.title',
    descriptionKey: 'shell.error.serverError.description',
    showCode: true,
  },
  503: {
    eyebrowKey: 'shell.error.offline.eyebrow',
    titleKey: 'shell.error.offline.title',
    descriptionKey: 'shell.error.offline.description',
    showCode: false,
  },
};

const copy = computed<ErrorCopy>(() => {
  const entry = COPY[props.error.statusCode] ?? COPY[500];
  return {
    eyebrow: t(entry.eyebrowKey),
    title: t(entry.titleKey),
    description: t(entry.descriptionKey),
    showCode: entry.showCode,
  };
});

const homeRoute = computed(() => (isAuthed.value ? '/dashboard' : '/'));
const homeLabel = computed(() => (isAuthed.value ? t('shell.error.backToDashboard') : t('shell.error.backToHome')));

const handleBack = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    window.history.back();
  } else {
    navigateTo(homeRoute.value);
  }
};

onMounted(() => {
  console.error('Error occurred:', props.error.message);
  Sentry.captureException(new Error(props.error.message));
});
</script>

<template>
  <div class="error-shell">
    <ClientOnly>
      <PortalTopNav v-if="isAuthed" />
      <div
        v-else
        class="flex h-[72px] items-center border-b border-black/[0.08] bg-white px-4 tablet:px-6 desktop:px-8"
      >
        <UILogo
          :to="'/'"
          :size="28"
          variant="compact"
          aria-label="Insiders of Dubai"
        />
      </div>
    </ClientOnly>

    <main class="error-main">
      <div class="error-card">
        <span class="eyebrow mono">{{ copy.eyebrow }}</span>
        <div class="code-wrap">
          <div
            v-if="copy.showCode"
            class="big-code font-serif"
            aria-hidden="true"
          >
            {{ error.statusCode }}
          </div>
          <h1 class="title font-serif">{{ copy.title }}</h1>
        </div>
        <p class="description">{{ copy.description }}</p>

        <div class="actions">
          <NuxtLink
            :to="homeRoute"
            class="btn btn-primary"
            >{{ homeLabel }}</NuxtLink
          >
          <button
            type="button"
            class="btn btn-secondary"
            @click="handleBack"
          >
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >←</span
            >
            {{ t('shell.error.goBack') }}
          </button>
        </div>

        <div class="footnote mono">
          <span
            class="dot"
            aria-hidden="true"
          />
          {{ t('shell.error.footnote') }}
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped lang="scss">
.error-shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at 20% 15%, rgba(255, 233, 200, 0.55), transparent 50%),
    radial-gradient(circle at 80% 85%, rgba(255, 224, 176, 0.45), transparent 55%), #faf6ec;
}

.error-main {
  flex: 1;
  display: grid;
  place-items: center;
  padding: 48px 16px 64px;
}

.mono {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.error-card {
  width: 100%;
  max-width: 560px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(13, 15, 21, 0.06);
  border-radius: 24px;
  padding: 36px 32px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  box-shadow: 0 4px 24px rgba(13, 15, 21, 0.04);
}

.eyebrow {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8b6000;
}

.code-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 6px;
}

.big-code {
  font-weight: 900;
  font-size: 168px;
  line-height: 0.9;
  letter-spacing: -0.04em;
  color: rgba(255, 182, 72, 0.22);
  user-select: none;
}
@media (max-width: 743px) {
  .big-code {
    font-size: 112px;
  }
}

.title {
  font-weight: 600;
  font-size: 30px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #0d0f15;
  margin: 0;
  max-width: 460px;
}
@media (max-width: 743px) {
  .title {
    font-size: 24px;
  }
}

.description {
  font-size: 15px;
  color: #4b4d52;
  line-height: 1.5;
  margin: 0;
  max-width: 440px;
}

.actions {
  margin-top: 8px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.btn {
  height: 42px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  padding-inline: 18px;
  border: 0;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}
.btn-primary {
  background: #0d0f15;
  color: #fff;
}
.btn-primary:hover {
  background: #25272c;
}
.btn-secondary {
  background: #fff;
  color: #0d0f15;
  border: 1px solid rgba(13, 15, 21, 0.1);
}
.btn-secondary:hover {
  background: #f3f3f3;
}

.footnote {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(13, 15, 21, 0.06);
  font-size: 11.5px;
  color: #6e6f73;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}
.footnote .dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #ffb648;
  display: inline-block;
}
</style>
